import React, { useContext, useEffect, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import CurrencyFormat from "react-currency-format";
import axios from "axios";
import authHeader from "../../UserServices/auth-header";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";
import DetailedSalesGraphicalView from "./DetailedSalesGraphicalView";
import PackageWiseSalesReport from "./PackageWiseSalesReport";
import BranchWiseComparison from "./BranchWiseComparison";

const DetailedSalesReport = () => {
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [viewMode, setViewMode] = useState("Detail"); // Detail, Summary, Management, TrainerWise, Graphical
  const [trainerWiseMode, setTrainerWiseMode] = useState("summary");
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const [collapsedDurations, setCollapsedDurations] = useState(new Map());

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedPackage, setSelectedPackage] = useState("");
  const [selectedDuration, setSelectedDuration] = useState("");
  const [selectedTrainer, setSelectedTrainer] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [selectedSaleType, setSelectedSaleType] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().endOf("month").format("YYYY-MM-DD"));

  // Available options for filters
  const [categories, setCategories] = useState([]);
  const [durations, setDurations] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageCatalog, setPackageCatalog] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [branches, setBranches] = useState([]);
  const [categoryMap, setCategoryMap] = useState(new Map());

  const formatDuration = (days) => {
    if (!days || days === 0) return "N/A";
    const months = Math.round(days / 30);
    if (months === 0) return `${days} Day${days !== 1 ? 's' : ''}`;
    if (days % 30 === 0) {
      if (months === 1) return "Monthly";
      if (months === 3) return "3 Months";
      if (months === 6) return "6 Months";
      if (months === 12) return "Annual";
      return `${months} Month${months !== 1 ? 's' : ''}`;
    }
    return `${days} Day${days !== 1 ? 's' : ''} (${months} Month${months !== 1 ? 's' : ''})`;
  };

  const getCategoryName = (category) => {
    const categoryMap = {
      "1": "Gym",
      "2": "Personal Trainer",
      "3": "Guest Pass",
      "4": "Small Group PT",
      "5": "Nutrition",
      "6": "Registration",
      "7": "Vitality Studio",
      "8": "Freezing",
      "9": "Physiotherapy",
      "10": "Cafe Sales",
      "11": "Vostro Fitness Academy",
      "12": "Massage Chair",
      "13": "Cafe Deposit",
      "14": "Physiotherapy Package",
      "15": "Vitality Studio",
      "16": "Befit"
    };
    return categoryMap[category] || "Unknown";
  };

  // Custom sort function to order categories according to client requirements
  const sortCategories = (categories) => {
    const categoryOrder = [
      "Registration",
      "Gym",
      "Personal Trainer",
      "Vitality Studio",
      "Vostro Fitness Academy",
      "Nutrition",
      "Physiotherapy",
      "Massage Chair",
      "Guest Pass",
      "Freezing",
      "Cafe Sales",
      "Cafe Deposit"
    ];
    
    return categories.sort((a, b) => {
      const aIndex = categoryOrder.indexOf(a);
      const bIndex = categoryOrder.indexOf(b);
      
      // If both categories are in the order list, sort by their position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only a is in the order list, it comes first
      if (aIndex !== -1) {
        return -1;
      }
      // If only b is in the order list, it comes first
      if (bIndex !== -1) {
        return 1;
      }
      // If neither is in the order list, sort alphabetically
      return a.localeCompare(b);
    });
  };

  const mergeUniqueSorted = (values) =>
    [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));

  const hydrateCategoryAndPackageOptions = async (filterBranchId = "") => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_Link + "/packages/all-with-categories",
        {
          params: {
            branch_id: filterBranchId || "",
            status: "2",
          },
          headers: authHeader(),
        }
      );

      if (response.status === 200 && response.data.status) {
        const rows = response.data.data || [];
        setPackageCatalog(rows);

        const normalizedCategoryMap = new Map();
        rows.forEach((item) => {
          const normalizedName = normalizeCategoryName(
            item.category_name || getCategoryName(item.category)
          );
          if (normalizedName && item.category) {
            normalizedCategoryMap.set(normalizedName, String(item.category));
          }
        });

        const categoryOptions = sortCategories(Array.from(normalizedCategoryMap.keys()));
        const packageOptions = mergeUniqueSorted(rows.map((item) => item.package_name));

        setCategoryMap(normalizedCategoryMap);
        setCategories(categoryOptions);
        setPackages(packageOptions);
      }
    } catch (error) {
      console.error("Error fetching sales report categories/packages:", error);
    }
  };

  const getDetailedSalesReport = async () => {
    setLoading(true);
    try {
      const params = {
        branch_id: branch_id,
      };

      if (selectedCategory) {
        params.category = categoryMap.get(selectedCategory) || selectedCategory;
      }
      if (selectedPackage) {
        // Fetch package ID by name from packages API
        try {
          const pkgResponse = await axios.get(
            process.env.REACT_APP_API_Link + "/packages/all-with-categories",
            {
              params: { branch_id: branch_id },
              headers: authHeader(),
            }
          );
          if (pkgResponse.status === 200) {
            const allPackages = pkgResponse.data.data || [];
            const packageItem = allPackages.find(pkg => pkg.package_name === selectedPackage);
            if (packageItem && packageItem.id) {
              params.package_id = packageItem.id;
            }
          }
        } catch (pkgErr) {
          console.error("Error fetching package ID:", pkgErr);
        }
      }
      if (selectedDuration) params.duration = selectedDuration;
      if (selectedTrainer) params.trainer_name = selectedTrainer;
      if (selectedPaymentMethod) params.payment_method_id = selectedPaymentMethod;
      if (selectedSaleType) params.sale_type = selectedSaleType;
      if (selectedBranch) params.filter_branch_id = selectedBranch;
      if (selectedSeller) params.seller_name = selectedSeller;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await axios.get(
        process.env.REACT_APP_API_Link + "/orders-detail/detailed-sales-report",
        {
          params: params,
          headers: authHeader(),
        }
      );

      if (response.status === 200 && response.data.status) {
        let data = response.data.data || [];
        if (selectedSaleType) {
          data = data.filter((item) => item.sale_type === selectedSaleType);
        }
        setSalesData(data);

        // Calculate summary
        const summary = {
          totalSales: data.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0),
          totalDiscount: data.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0),
          totalTax: data.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0),
          totalNetPrice: data.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0),
          totalReceived: data.reduce((sum, item) => sum + (parseFloat(item.total_received) || 0), 0),
          totalPending: data.reduce((sum, item) => sum + (parseFloat(item.total_pending) || 0), 0),
          totalRecords: data.length,
        };
        setSummaryData(summary);

        // Keep filters hydrated from the catalog source, but enrich them from live result rows too.
        if (data.length > 0) {
          const resultCategoryMap = new Map(categoryMap);
          data.forEach(item => {
            if (item.category_name && item.category) {
              resultCategoryMap.set(
                normalizeCategoryName(item.category_name),
                String(item.category)
              );
            }
          });
          setCategoryMap(resultCategoryMap);
          setCategories(sortCategories(Array.from(resultCategoryMap.keys())));
          
          const catalogPackages = packageCatalog.map((item) => item.package_name);
          const resultPackages = data.map(item => item.package_name).filter(Boolean);
          setPackages(mergeUniqueSorted([...catalogPackages, ...resultPackages]));
          
          // Extract sellers for filter
          const uniqueSellers = [...new Set(data.map(item => item.sold_by).filter(Boolean))].sort();
          setSellers(uniqueSellers);
          
          // Extract durations
          const uniqueDurations = [...new Set(data.map(item => formatDuration(item.package_duration)))].sort((a, b) => {
            const order = { "Monthly": 1, "3 Months": 2, "6 Months": 3, "Annual": 4 };
            const aOrder = order[a] || 99;
            const bOrder = order[b] || 99;
            if (aOrder !== bOrder) return aOrder - bOrder;
            return a.localeCompare(b);
          });
          setDurations(uniqueDurations);
          
          // Extract trainers
          const uniqueTrainers = [...new Set(data.map(item => item.trainer_name).filter(Boolean))].sort();
          setTrainers(uniqueTrainers);
        } else {
          setSellers([]);
          setDurations([]);
          setTrainers([]);
        }
      } else {
        setSalesData([]);
        setSummaryData(null);
      }
    } catch (err) {
      console.error("Error fetching detailed sales report:", err);
      setSalesData([]);
      setSummaryData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (branch_id) {
      hydrateCategoryAndPackageOptions(selectedBranch);
      getDetailedSalesReport();
      // Fetch payment methods
      const fetchPaymentMethods = async () => {
        try {
          const response = await axios.get(
            process.env.REACT_APP_API_Link + "/related_things/get-names-list",
            {
              params: {
                type: "PaymentMethod",
              },
              headers: authHeader(),
            }
          );
          if (response.status === 200 && response.data.status) {
            const methods = response.data.data || [];
            setPaymentMethods(methods);
          }
        } catch (err) {
          console.error("Error fetching payment methods:", err);
        }
      };
      fetchPaymentMethods();
      
      // Fetch branches
      const fetchBranches = async () => {
        try {
          const response = await axios.get(
            process.env.REACT_APP_API_Link + "/branches/branches-name-list",
            {
              params: {
                status: "1", // Active branches
              },
              headers: authHeader(),
            }
          );
          if (response.status === 200 && response.data.status) {
            const branchesList = response.data.data || [];
            setBranches(branchesList);
          }
        } catch (err) {
          console.error("Error fetching branches:", err);
        }
      };
      fetchBranches();
    }
  }, [branch_id]);

  // Refresh data when branch filter changes
  useEffect(() => {
    if (branch_id) {
      hydrateCategoryAndPackageOptions(selectedBranch);
      // Reset dependent filters when branch changes (but keep branch, payment method, sale type, and dates)
      setSelectedCategory("");
      setSelectedPackage("");
      setSelectedDuration("");
      setSelectedTrainer("");
      setSelectedSeller("");
      // Refresh data to get new filter options for the selected branch (or all branches if none selected)
      // Use setTimeout to ensure state updates are processed first
      setTimeout(() => {
        getDetailedSalesReport();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  // Reset filters and set dates when Branch Wise tab is selected
  useEffect(() => {
    if (viewMode === "BranchWise" && branch_id) {
      // Reset all filters
      setSelectedCategory("");
      setSelectedPackage("");
      setSelectedDuration("");
      setSelectedTrainer("");
      setSelectedPaymentMethod("");
      setSelectedSaleType("");
      setSelectedBranch("");
      setSelectedSeller("");
      
      // Set dates to one month (current month)
      setStartDate(moment().startOf("month").format("YYYY-MM-DD"));
      setEndDate(moment().endOf("month").format("YYYY-MM-DD"));
      
      // Refresh data after a short delay to ensure state updates are processed
      setTimeout(() => {
        getDetailedSalesReport();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const handleSearch = () => {
    getDetailedSalesReport();
  };

  const handleClearFilters = () => {
    setSelectedCategory("");
    setSelectedPackage("");
    setSelectedDuration("");
    setSelectedTrainer("");
    setSelectedPaymentMethod("");
    setSelectedSaleType("");
    setSelectedBranch("");
    setSelectedSeller("");
    setStartDate(moment().startOf("month").format("YYYY-MM-DD"));
    setEndDate(moment().endOf("month").format("YYYY-MM-DD"));
  };

  // Toggle category collapse
  const toggleCategory = (categoryName) => {
    setCollapsedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  // Toggle duration collapse
  const toggleDuration = (categoryName, durationName) => {
    const key = `${categoryName}-${durationName}`;
    setCollapsedDurations(prev => {
      const newMap = new Map(prev);
      if (newMap.has(key)) {
        newMap.delete(key);
      } else {
        newMap.set(key, true);
      }
      return newMap;
    });
  };

  const handleDateFilter = (period) => {
    const today = moment();
    let start, end;

    switch(period) {
      case "1month":
        start = today.clone().subtract(1, 'months').startOf('month');
        end = today.clone().subtract(1, 'months').endOf('month');
        break;
      case "2month":
        start = today.clone().subtract(2, 'months').startOf('month');
        end = today.clone().subtract(1, 'months').endOf('month');
        break;
      case "3month":
        start = today.clone().subtract(3, 'months').startOf('month');
        end = today.clone().subtract(1, 'months').endOf('month');
        break;
      case "6month":
        start = today.clone().subtract(6, 'months').startOf('month');
        end = today.clone().subtract(1, 'months').endOf('month');
        break;
      case "quarterly":
        // Current quarter (last completed quarter - 3 months)
        start = today.clone().subtract(3, 'months').startOf('month');
        end = today.clone().subtract(1, 'months').endOf('month');
        break;
      case "yearly":
        start = today.clone().subtract(1, 'year').startOf('month');
        end = today.clone().subtract(1, 'months').endOf('month');
        break;
      default:
        start = today.clone().startOf('month');
        end = today.clone().endOf('month');
    }

    setStartDate(start.format("YYYY-MM-DD"));
    setEndDate(end.format("YYYY-MM-DD"));
  };

  // Normalize category name to match client's naming convention
  const normalizeCategoryName = (categoryName) => {
    if (!categoryName) return "Uncategorized";
    
    // Map database names to display names
    const nameMap = {
      "Guest pass": "Guest Pass",
      "Guest": "Guest Pass",
      "Cafe": "Cafe Sales",
      "Cafe Deposits": "Cafe Deposit",
      "Cafe Deposit": "Cafe Deposit",
      "Session": "Vitality Studio",
      "Bootcamp": "Vitality Studio",
      "GX Package": "Vitality Studio",
      "CFT": "Vostro Fitness Academy",
      "General": "Physiotherapy",
      "Physiotherapy Package": "Physiotherapy",
      "Nutritionist": "Nutrition",
      "Nutrition": "Nutrition"
    };
    
    return nameMap[categoryName] || categoryName;
  };

  // Group data by category and duration
  const groupDataByCategoryAndDuration = (data) => {
    const grouped = {};
    
    data.forEach(item => {
      let categoryName = item.category_name || getCategoryName(item.category) || "Uncategorized";
      categoryName = normalizeCategoryName(categoryName);
      const durationName = formatDuration(item.package_duration);
      
      if (!grouped[categoryName]) {
        grouped[categoryName] = {};
      }
      if (!grouped[categoryName][durationName]) {
        grouped[categoryName][durationName] = [];
      }
      grouped[categoryName][durationName].push(item);
    });
    
    return grouped;
  };

  const groupDataByTrainer = (data) => {
    const grouped = {};

    data.forEach((item) => {
      const trainerName = item.trainer_name?.trim();
      if (!trainerName) {
        return;
      }
      if (!grouped[trainerName]) {
        grouped[trainerName] = [];
      }
      grouped[trainerName].push(item);
    });

    return Object.fromEntries(
      Object.entries(grouped).sort(([, itemsA], [, itemsB]) => {
        const totalA = itemsA.reduce(
          (sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0),
          0
        );
        const totalB = itemsB.reduce(
          (sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0),
          0
        );
        return totalB - totalA;
      })
    );
  };

  const getTrainerWiseTitle = () =>
    trainerWiseMode === "detail"
      ? "Trainer Wise Detailed Sales Report"
      : "Trainer Wise Summary Report";

  // Get filter information string
  const getFilterInfo = () => {
    const filters = [];
    if (selectedBranch) {
      const branchName = branches.find(b => b.id == selectedBranch)?.name || selectedBranch;
      filters.push(`Branch: ${branchName}`);
    } else {
      filters.push("Branch: All Branches");
    }
    if (selectedCategory) filters.push(`Category: ${selectedCategory}`);
    if (selectedPackage) filters.push(`Package: ${selectedPackage}`);
    if (selectedDuration) filters.push(`Duration: ${selectedDuration}`);
    if (selectedTrainer) filters.push(`Trainer: ${selectedTrainer}`);
    if (selectedPaymentMethod) {
      const methodName = paymentMethods.find(m => m.id == selectedPaymentMethod)?.name || selectedPaymentMethod;
      filters.push(`Payment Method: ${methodName}`);
    }
    if (selectedSaleType) filters.push(`Sale Type: ${selectedSaleType}`);
    if (selectedSeller) filters.push(`Sold By: ${selectedSeller}`);
    if (startDate) filters.push(`From Date: ${moment(startDate).format("DD/MM/YYYY")}`);
    if (endDate) filters.push(`To Date: ${moment(endDate).format("DD/MM/YYYY")}`);
    return filters.join(" | ");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    let yPos = 15;

    // Title based on view mode
    const viewModeTitles = {
      "Detail": "Detailed Sales Report",
      "Summary": "Sales Summary Report",
      "Management": "Management View Report",
      "TrainerWise": "Trainer Wise Sales Report",
      "BranchWise": "Branch Wise Comparison Report",
      "PackageWise": "Package Wise Sales Report",
      "NewVsRenewal": "New vs Renewal Comparison Report",
      "Graphical": "Graphical Sales Report"
    };
    const reportTitle = viewMode === "TrainerWise"
      ? getTrainerWiseTitle()
      : (viewModeTitles[viewMode] || "Sales Report");
    
    doc.setFontSize(16);
    doc.text(reportTitle, margin, yPos);
    yPos += 7;

    // Filter information
    doc.setFontSize(9);
    const filterInfo = getFilterInfo();
    const splitFilters = doc.splitTextToSize(filterInfo, pageWidth - 2 * margin);
    splitFilters.forEach((line, index) => {
      doc.text(line, margin, yPos + (index * 4));
    });
    yPos += (splitFilters.length * 4) + 3;

    // Date generated
    doc.setFontSize(8);
    doc.text(`Generated: ${moment().format("DD/MM/YYYY HH:mm")}`, margin, yPos);
    yPos += 8;

    // Export based on view mode
    if (viewMode === "Detail") {
      // Detail view - export table data
      const tableData = salesData.map((item, index) => [
        index + 1,
        item.order_id || "N/A",
        item.branch_name || "N/A",
        item.package_name || "N/A",
        item.category_name || getCategoryName(item.category) || "N/A",
        formatDuration(item.package_duration),
        item.trainer_name || "N/A",
        item.client_name || "N/A",
        item.sale_type || "N/A",
        `Rs ${parseFloat(item.order_detail_price || 0).toFixed(2)}`,
        `Rs ${parseFloat(item.order_detail_discount || 0).toFixed(2)}`,
        `Rs ${parseFloat(item.order_detail_tax || 0).toFixed(2)}`,
        `Rs ${parseFloat(item.order_detail_net_price || 0).toFixed(2)}`,
        item.payment_method || "N/A",
        item.sold_by || "N/A",
        moment(item.sale_date).format("DD/MM/YYYY"),
      ]);

      doc.autoTable({
        startY: yPos,
        head: [[
          "No", "Order ID", "Branch", "Package", "Category", "Duration", "Trainer", "Client", "Sale Type",
          "Price", "Discount", "Tax", "Net Price", "Payment Method", "Sold By", "Sale Date"
        ]],
        body: tableData,
        styles: { fontSize: 6 },
        headStyles: { fillColor: [66, 139, 202] },
        margin: { left: margin, right: margin },
      });
    } else if (viewMode === "Summary") {
      // Summary view - export grouped summary
      const groupedData = groupDataByCategoryAndDuration(salesData);
      const summaryData_array = [];
      
      sortCategories(Object.keys(groupedData)).forEach(categoryName => {
        const durationGroups = groupedData[categoryName];
        Object.keys(durationGroups).sort().forEach(durationName => {
          const items = durationGroups[durationName];
          const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
          const totalNetPrice = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
          summaryData_array.push([
            categoryName,
            durationName,
            items.length,
            `Rs ${totalPrice.toFixed(2)}`,
            `Rs ${totalNetPrice.toFixed(2)}`
          ]);
        });
      });

      doc.autoTable({
        startY: yPos,
        head: [["Category", "Duration", "Quantity", "Total Price", "Total Net Price"]],
        body: summaryData_array,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [66, 139, 202] },
        margin: { left: margin, right: margin },
      });
    } else if (viewMode === "Management") {
      // Management view - export category totals with New/Renew breakdown
      const groupedData = groupDataByCategoryAndDuration(salesData);
      const managementData = [];
      
      sortCategories(Object.keys(groupedData)).forEach(categoryName => {
        const durationGroups = groupedData[categoryName];
        const allItems = Object.values(durationGroups).flat();
        const newItems = allItems.filter(item => item.sale_type === "New");
        const renewItems = allItems.filter(item => item.sale_type === "Renew");
        
        const newPrice = newItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
        const renewPrice = renewItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
        const newDiscount = newItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
        const renewDiscount = renewItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
        const newTax = newItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
        const renewTax = renewItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
        const newNet = newItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
        const renewNet = renewItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
        
        managementData.push([
          categoryName,
          `Rs ${newPrice.toFixed(2)}`,
          `Rs ${renewPrice.toFixed(2)}`,
          `Rs ${(newPrice + renewPrice).toFixed(2)}`,
          `Rs ${newDiscount.toFixed(2)}`,
          `Rs ${renewDiscount.toFixed(2)}`,
          `Rs ${(newDiscount + renewDiscount).toFixed(2)}`,
          `Rs ${newTax.toFixed(2)}`,
          `Rs ${renewTax.toFixed(2)}`,
          `Rs ${(newTax + renewTax).toFixed(2)}`,
          `Rs ${newNet.toFixed(2)}`,
          `Rs ${renewNet.toFixed(2)}`,
          `Rs ${(newNet + renewNet).toFixed(2)}`
        ]);
      });

      doc.autoTable({
        startY: yPos,
        head: [[
          "Category", "Price (New)", "Price (Renew)", "Price (Total)",
          "Discount (New)", "Discount (Renew)", "Discount (Total)",
          "Tax (New)", "Tax (Renew)", "Tax (Total)",
          "Net Price (New)", "Net Price (Renew)", "Net Price (Total)"
        ]],
        body: managementData,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [66, 139, 202] },
        margin: { left: margin, right: margin },
      });
    } else if (viewMode === "BranchWise") {
      // Branch Wise - export comparison data
      const f11Data = salesData.filter(item => 
        (item.branch_id === 15 || item.branch_id === '15') || 
        (item.branch_name && (item.branch_name.toLowerCase().includes('f11') || item.branch_name.toLowerCase().includes('f-11')))
      );
      const g13Data = salesData.filter(item => 
        (item.branch_id === 1 || item.branch_id === '1') || 
        (item.branch_name && (item.branch_name.toLowerCase().includes('g13') || item.branch_name.toLowerCase().includes('g-13')))
      );
      
      const f11Total = f11Data.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
      const g13Total = g13Data.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
      
      doc.setFontSize(12);
      doc.text("Branch Comparison", margin, yPos);
      yPos += 8;
      
      const branchComparisonData = [
        ["Branch", "Total Sales", "Quantity", "New Sales", "Renew Sales"],
        ["F-11", `Rs ${f11Total.toFixed(2)}`, f11Data.length, 
         `Rs ${f11Data.filter(i => i.sale_type === "New").reduce((s, i) => s + (parseFloat(i.order_detail_net_price) || 0), 0).toFixed(2)}`,
         `Rs ${f11Data.filter(i => i.sale_type === "Renew").reduce((s, i) => s + (parseFloat(i.order_detail_net_price) || 0), 0).toFixed(2)}`],
        ["G-13", `Rs ${g13Total.toFixed(2)}`, g13Data.length,
         `Rs ${g13Data.filter(i => i.sale_type === "New").reduce((s, i) => s + (parseFloat(i.order_detail_net_price) || 0), 0).toFixed(2)}`,
         `Rs ${g13Data.filter(i => i.sale_type === "Renew").reduce((s, i) => s + (parseFloat(i.order_detail_net_price) || 0), 0).toFixed(2)}`],
      ];

      doc.autoTable({
        startY: yPos,
        head: [branchComparisonData[0]],
        body: branchComparisonData.slice(1),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [66, 139, 202] },
        margin: { left: margin, right: margin },
      });
    } else if (viewMode === "TrainerWise") {
      const groupedTrainerData = groupDataByTrainer(salesData);

      if (trainerWiseMode === "detail") {
        const trainerRows = [];

        Object.entries(groupedTrainerData).forEach(([trainerName, items]) => {
          items.forEach((item) => {
            trainerRows.push([
              trainerName,
              item.client_name || "N/A",
              item.package_name || "N/A",
              item.category_name || getCategoryName(item.category) || "N/A",
              item.sale_type || "N/A",
              `Rs ${parseFloat(item.order_detail_price || 0).toFixed(2)}`,
              `Rs ${parseFloat(item.order_detail_discount || 0).toFixed(2)}`,
              `Rs ${parseFloat(item.order_detail_tax || 0).toFixed(2)}`,
              `Rs ${parseFloat(item.order_detail_net_price || 0).toFixed(2)}`,
              item.payment_method || "N/A",
              item.sold_by || "N/A",
              moment(item.sale_date).format("DD/MM/YYYY"),
            ]);
          });
        });

        doc.autoTable({
          startY: yPos,
          head: [[
            "Trainer", "Client", "Package", "Category", "Sale Type", "Price",
            "Discount", "Tax", "Net Price", "Payment Method", "Sold By", "Sale Date"
          ]],
          body: trainerRows,
          styles: { fontSize: 7, cellWidth: "wrap" },
          headStyles: { fillColor: [66, 139, 202] },
          margin: { left: margin, right: margin },
        });
      } else {
        const trainerRows = [];

        Object.entries(groupedTrainerData).forEach(([trainerName, items]) => {
          const totalPrice = items.reduce(
            (sum, item) => sum + (parseFloat(item.order_detail_price) || 0),
            0
          );
          const totalDiscount = items.reduce(
            (sum, item) => sum + (parseFloat(item.order_detail_discount) || 0),
            0
          );
          const totalTax = items.reduce(
            (sum, item) => sum + (parseFloat(item.order_detail_tax) || 0),
            0
          );
          const totalNet = items.reduce(
            (sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0),
            0
          );
          const uniqueClients = new Set(items.map((item) => item.client_name).filter(Boolean)).size;

          trainerRows.push([
            trainerName,
            items.length,
            uniqueClients,
            `Rs ${totalPrice.toFixed(2)}`,
            `Rs ${totalDiscount.toFixed(2)}`,
            `Rs ${totalTax.toFixed(2)}`,
            `Rs ${totalNet.toFixed(2)}`,
          ]);
        });

        doc.autoTable({
          startY: yPos,
          head: [[
            "Trainer", "Sales Count", "Client Count", "Total Price",
            "Discount", "Tax", "Total Net Sales"
          ]],
          body: trainerRows,
          styles: { fontSize: 8, cellWidth: "wrap" },
          headStyles: { fillColor: [66, 139, 202] },
          margin: { left: margin, right: margin },
        });
      }
    } else {
      // For other views, export basic table data
      const tableData = salesData.map((item, index) => [
        index + 1,
        item.order_id || "N/A",
        item.package_name || "N/A",
        item.category_name || getCategoryName(item.category) || "N/A",
        `Rs ${parseFloat(item.order_detail_net_price || 0).toFixed(2)}`,
        moment(item.sale_date).format("DD/MM/YYYY"),
      ]);

      doc.autoTable({
        startY: yPos,
        head: [["No", "Order ID", "Package", "Category", "Net Price", "Sale Date"]],
        body: tableData,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [66, 139, 202] },
        margin: { left: margin, right: margin },
      });
    }

    doc.save(`${reportTitle.replace(/\s+/g, '_')}_${moment().format("YYYY-MM-DD")}.pdf`);
  };

  const exportToCSV = () => {
    const viewModeTitles = {
      "Detail": "Detailed_Sales_Report",
      "Summary": "Sales_Summary_Report",
      "Management": "Management_View_Report",
      "TrainerWise": "Trainer_Wise_Sales_Report",
      "BranchWise": "Branch_Wise_Comparison_Report",
      "PackageWise": "Package_Wise_Sales_Report",
      "NewVsRenewal": "New_vs_Renewal_Comparison_Report",
      "Graphical": "Graphical_Sales_Report"
    };
    const reportTitle = viewMode === "TrainerWise"
      ? getTrainerWiseTitle().replace(/\s+/g, "_")
      : (viewModeTitles[viewMode] || "Sales_Report");
    const fileName = reportTitle;

    let csv = "";
    
    // Add header section with formatting
    csv += "=".repeat(80) + "\n";
    csv += `REPORT: ${reportTitle.replace(/_/g, ' ').toUpperCase()}\n`;
    csv += "=".repeat(80) + "\n\n";
    
    // Add filter information as header with visual separation
    csv += "-".repeat(80) + "\n";
    csv += "FILTER INFORMATION\n";
    csv += "-".repeat(80) + "\n";
    const filterInfo = getFilterInfo();
    // Split filter info into multiple lines if needed
    const filterLines = filterInfo.split(" | ");
    filterLines.forEach(line => {
      csv += line + "\n";
    });
    csv += "\n";
    csv += `Generated: ${moment().format("DD/MM/YYYY HH:mm")}\n`;
    csv += "-".repeat(80) + "\n\n";
    
    // Add summary totals if available
    if (summaryData && (viewMode === "Detail" || viewMode === "Summary")) {
      csv += "-".repeat(80) + "\n";
      csv += "SUMMARY TOTALS\n";
      csv += "-".repeat(80) + "\n";
      csv += `Total Records: ${summaryData.totalRecords}\n`;
      csv += `Total Sales (Excl. GST): Rs ${summaryData.totalSales.toFixed(2)}\n`;
      csv += `Total Discount: Rs ${summaryData.totalDiscount.toFixed(2)}\n`;
      csv += `Total Tax (GST): Rs ${summaryData.totalTax.toFixed(2)}\n`;
      csv += `Total Net Price (Incl. GST): Rs ${summaryData.totalNetPrice.toFixed(2)}\n`;
      csv += "-".repeat(80) + "\n\n";
    }

    // Export based on view mode
    if (viewMode === "Detail") {
      csv += "-".repeat(80) + "\n";
      csv += "DETAILED SALES DATA\n";
      csv += "-".repeat(80) + "\n\n";
      
      const headers = [
        "No", "Order ID", "Branch", "Package Name", "Category", "Duration", "Trainer Name",
        "Client Name", "Sale Type", "Price", "Discount", "Tax", "Net Price",
        "Payment Method", "Sold By", "Sale Date"
      ];

      // Header row with uppercase and bold-like appearance
      csv += headers.map(h => `"${h.toUpperCase()}"`).join(",") + "\n";
      
      // Group data by category and duration for better organization
      const groupedData = groupDataByCategoryAndDuration(salesData);
      let globalIndex = 1;
      
      sortCategories(Object.keys(groupedData)).forEach(categoryName => {
        const durationGroups = groupedData[categoryName];
        Object.keys(durationGroups).sort().forEach(durationName => {
          const items = durationGroups[durationName];
          
          // Add category and duration separator
          csv += `\n"--- ${categoryName} - ${durationName} (${items.length} sales) ---",,,,,,,,,,,,,\n`;
          
          items.forEach((item, idx) => {
            const row = [
              globalIndex++,
              item.order_id || "",
              item.branch_name || "",
              item.package_name || "",
              item.category_name || getCategoryName(item.category) || "",
              formatDuration(item.package_duration),
              item.trainer_name || "",
              item.client_name || "",
              item.sale_type || "",
              item.order_detail_price || 0,
              item.order_detail_discount || 0,
              item.order_detail_tax || 0,
              item.order_detail_net_price || 0,
              item.payment_method || "",
              item.sold_by || "",
              moment(item.sale_date).format("DD/MM/YYYY"),
            ];
            csv += row.map(cell => `"${cell}"`).join(",") + "\n";
          });
          
          // Add subtotal for this duration
          const subtotalPrice = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
          const subtotalDiscount = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
          const subtotalTax = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
          const subtotalNet = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
          csv += `"Subtotal",,,,,,,"${durationName}",,${subtotalPrice.toFixed(2)},${subtotalDiscount.toFixed(2)},${subtotalTax.toFixed(2)},${subtotalNet.toFixed(2)},,,\n`;
        });
      });
      
      csv += "\n";
    } else if (viewMode === "Summary") {
      csv += "-".repeat(80) + "\n";
      csv += "SALES SUMMARY BY CATEGORY AND DURATION\n";
      csv += "-".repeat(80) + "\n\n";
      
      const headers = ["Category", "Duration", "Quantity", "Total Price", "Total Net Price"];
      csv += headers.map(h => `"${h.toUpperCase()}"`).join(",") + "\n";
      
      const groupedData = groupDataByCategoryAndDuration(salesData);
      let categoryTotalQuantity = 0;
      let categoryTotalPrice = 0;
      let categoryTotalNet = 0;
      let grandTotalQuantity = 0;
      let grandTotalPrice = 0;
      let grandTotalNet = 0;
      
      sortCategories(Object.keys(groupedData)).forEach((categoryName, catIdx) => {
        const durationGroups = groupedData[categoryName];
        categoryTotalQuantity = 0;
        categoryTotalPrice = 0;
        categoryTotalNet = 0;
        
        Object.keys(durationGroups).sort().forEach(durationName => {
          const items = durationGroups[durationName];
          const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
          const totalNetPrice = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
          csv += `"${categoryName}","${durationName}",${items.length},${totalPrice.toFixed(2)},${totalNetPrice.toFixed(2)}\n`;
          
          categoryTotalQuantity += items.length;
          categoryTotalPrice += totalPrice;
          categoryTotalNet += totalNetPrice;
        });
        
        // Category subtotal
        csv += `"${categoryName} - TOTAL",,${categoryTotalQuantity},${categoryTotalPrice.toFixed(2)},${categoryTotalNet.toFixed(2)}\n`;
        csv += `"",,,,,` + "\n";
        
        grandTotalQuantity += categoryTotalQuantity;
        grandTotalPrice += categoryTotalPrice;
        grandTotalNet += categoryTotalNet;
      });
      
      // Grand total
      csv += "-".repeat(80) + "\n";
      csv += `"GRAND TOTAL",,${grandTotalQuantity},${grandTotalPrice.toFixed(2)},${grandTotalNet.toFixed(2)}\n`;
      csv += "-".repeat(80) + "\n";
    } else if (viewMode === "Management") {
      csv += "-".repeat(80) + "\n";
      csv += "MANAGEMENT VIEW - CATEGORY TOTALS WITH NEW/RENEW BREAKDOWN\n";
      csv += "-".repeat(80) + "\n\n";
      
      const headers = [
        "Category", "Price (New)", "Price (Renew)", "Price (Total)",
        "Discount (New)", "Discount (Renew)", "Discount (Total)",
        "Tax (New)", "Tax (Renew)", "Tax (Total)",
        "Net Price (New)", "Net Price (Renew)", "Net Price (Total)"
      ];
      csv += headers.map(h => `"${h.toUpperCase()}"`).join(",") + "\n";
      csv += "-".repeat(80) + "\n";
      
      const groupedData = groupDataByCategoryAndDuration(salesData);
      let grandNewPrice = 0, grandRenewPrice = 0, grandNewDiscount = 0, grandRenewDiscount = 0;
      let grandNewTax = 0, grandRenewTax = 0, grandNewNet = 0, grandRenewNet = 0;
      
      sortCategories(Object.keys(groupedData)).forEach(categoryName => {
        const durationGroups = groupedData[categoryName];
        const allItems = Object.values(durationGroups).flat();
        const newItems = allItems.filter(item => item.sale_type === "New");
        const renewItems = allItems.filter(item => item.sale_type === "Renew");
        
        const newPrice = newItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
        const renewPrice = renewItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
        const newDiscount = newItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
        const renewDiscount = renewItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
        const newTax = newItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
        const renewTax = renewItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
        const newNet = newItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
        const renewNet = renewItems.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
        
        csv += `"${categoryName}",${newPrice.toFixed(2)},${renewPrice.toFixed(2)},${(newPrice + renewPrice).toFixed(2)},${newDiscount.toFixed(2)},${renewDiscount.toFixed(2)},${(newDiscount + renewDiscount).toFixed(2)},${newTax.toFixed(2)},${renewTax.toFixed(2)},${(newTax + renewTax).toFixed(2)},${newNet.toFixed(2)},${renewNet.toFixed(2)},${(newNet + renewNet).toFixed(2)}\n`;
        
        grandNewPrice += newPrice;
        grandRenewPrice += renewPrice;
        grandNewDiscount += newDiscount;
        grandRenewDiscount += renewDiscount;
        grandNewTax += newTax;
        grandRenewTax += renewTax;
        grandNewNet += newNet;
        grandRenewNet += renewNet;
      });
      
      // Grand totals
      csv += "-".repeat(80) + "\n";
      csv += `"GRAND TOTAL",${grandNewPrice.toFixed(2)},${grandRenewPrice.toFixed(2)},${(grandNewPrice + grandRenewPrice).toFixed(2)},${grandNewDiscount.toFixed(2)},${grandRenewDiscount.toFixed(2)},${(grandNewDiscount + grandRenewDiscount).toFixed(2)},${grandNewTax.toFixed(2)},${grandRenewTax.toFixed(2)},${(grandNewTax + grandRenewTax).toFixed(2)},${grandNewNet.toFixed(2)},${grandRenewNet.toFixed(2)},${(grandNewNet + grandRenewNet).toFixed(2)}\n`;
      csv += "-".repeat(80) + "\n";
    } else if (viewMode === "BranchWise") {
      csv += "-".repeat(80) + "\n";
      csv += "BRANCH WISE COMPARISON (F-11 vs G-13)\n";
      csv += "-".repeat(80) + "\n\n";
      
      const headers = ["Branch", "Total Sales", "Quantity", "New Sales", "Renew Sales"];
      csv += headers.map(h => `"${h.toUpperCase()}"`).join(",") + "\n";
      csv += "-".repeat(80) + "\n";
      
      const f11Data = salesData.filter(item => 
        (item.branch_id === 15 || item.branch_id === '15') || 
        (item.branch_name && (item.branch_name.toLowerCase().includes('f11') || item.branch_name.toLowerCase().includes('f-11')))
      );
      const g13Data = salesData.filter(item => 
        (item.branch_id === 1 || item.branch_id === '1') || 
        (item.branch_name && (item.branch_name.toLowerCase().includes('g13') || item.branch_name.toLowerCase().includes('g-13')))
      );
      
      const f11Total = f11Data.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
      const g13Total = g13Data.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
      const f11New = f11Data.filter(i => i.sale_type === "New").reduce((s, i) => s + (parseFloat(i.order_detail_net_price) || 0), 0);
      const f11Renew = f11Data.filter(i => i.sale_type === "Renew").reduce((s, i) => s + (parseFloat(i.order_detail_net_price) || 0), 0);
      const g13New = g13Data.filter(i => i.sale_type === "New").reduce((s, i) => s + (parseFloat(i.order_detail_net_price) || 0), 0);
      const g13Renew = g13Data.filter(i => i.sale_type === "Renew").reduce((s, i) => s + (parseFloat(i.order_detail_net_price) || 0), 0);
      
      csv += `"F-11",${f11Total.toFixed(2)},${f11Data.length},${f11New.toFixed(2)},${f11Renew.toFixed(2)}\n`;
      csv += `"G-13",${g13Total.toFixed(2)},${g13Data.length},${g13New.toFixed(2)},${g13Renew.toFixed(2)}\n`;
      csv += "-".repeat(80) + "\n";
      csv += `"TOTAL",${(f11Total + g13Total).toFixed(2)},${f11Data.length + g13Data.length},${(f11New + g13New).toFixed(2)},${(f11Renew + g13Renew).toFixed(2)}\n`;
      csv += "-".repeat(80) + "\n";
    } else if (viewMode === "TrainerWise") {
      csv += "-".repeat(80) + "\n";
      csv += `${getTrainerWiseTitle().toUpperCase()}\n`;
      csv += "-".repeat(80) + "\n\n";

      const groupedTrainerData = groupDataByTrainer(salesData);

      if (trainerWiseMode === "detail") {
        const headers = [
          "Trainer",
          "Client",
          "Package",
          "Category",
          "Sale Type",
          "Price",
          "Discount",
          "Tax",
          "Net Price",
          "Payment Method",
          "Sold By",
          "Sale Date"
        ];
        csv += headers.map(h => `"${h.toUpperCase()}"`).join(",") + "\n";
        csv += "-".repeat(80) + "\n";

        Object.entries(groupedTrainerData).forEach(([trainerName, items]) => {
          csv += `\n"--- ${trainerName} (${items.length} sales) ---",,,,,,,,,,,\n`;

          items.forEach((item) => {
            const row = [
              trainerName,
              item.client_name || "",
              item.package_name || "",
              item.category_name || getCategoryName(item.category) || "",
              item.sale_type || "",
              parseFloat(item.order_detail_price || 0).toFixed(2),
              parseFloat(item.order_detail_discount || 0).toFixed(2),
              parseFloat(item.order_detail_tax || 0).toFixed(2),
              parseFloat(item.order_detail_net_price || 0).toFixed(2),
              item.payment_method || "",
              item.sold_by || "",
              moment(item.sale_date).format("DD/MM/YYYY"),
            ];
            csv += row.map(cell => `"${cell}"`).join(",") + "\n";
          });

          const trainerTotal = items.reduce(
            (sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0),
            0
          );
          csv += `"${trainerName} TOTAL",,,,,,,,${trainerTotal.toFixed(2)},,,\n`;
        });
      } else {
        const headers = [
          "Trainer",
          "Sales Count",
          "Client Count",
          "Total Price",
          "Discount",
          "Tax",
          "Net Price"
        ];
        csv += headers.map(h => `"${h.toUpperCase()}"`).join(",") + "\n";
        csv += "-".repeat(80) + "\n";

        Object.entries(groupedTrainerData).forEach(([trainerName, items]) => {
          const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
          const totalDiscount = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
          const totalTax = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
          const totalNet = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
          const uniqueClients = new Set(items.map((item) => item.client_name).filter(Boolean)).size;

          csv += [
            trainerName,
            items.length,
            uniqueClients,
            totalPrice.toFixed(2),
            totalDiscount.toFixed(2),
            totalTax.toFixed(2),
            totalNet.toFixed(2)
          ].map(cell => `"${cell}"`).join(",") + "\n";
        });
      }

      csv += "-".repeat(80) + "\n";
    } else {
      // For other views, export basic data
      csv += "-".repeat(80) + "\n";
      csv += "SALES DATA\n";
      csv += "-".repeat(80) + "\n\n";
      
      const headers = ["No", "Order ID", "Package", "Category", "Net Price", "Sale Date"];
      csv += headers.map(h => `"${h.toUpperCase()}"`).join(",") + "\n";
      csv += "-".repeat(80) + "\n";
      
      salesData.forEach((item, index) => {
        csv += `${index + 1},"${item.order_id || ""}","${item.package_name || ""}","${item.category_name || getCategoryName(item.category) || ""}",${parseFloat(item.order_detail_net_price || 0).toFixed(2)},"${moment(item.sale_date).format("DD/MM/YYYY")}"\n`;
      });
      
      // Add total
      const totalNet = salesData.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
      csv += "-".repeat(80) + "\n";
      csv += `"TOTAL",,,,${totalNet.toFixed(2)},\n`;
      csv += "-".repeat(80) + "\n";
    }
    
    // Add footer
    csv += "\n";
    csv += "=".repeat(80) + "\n";
    csv += `END OF REPORT - Generated on ${moment().format("DD/MM/YYYY HH:mm")}\n`;
    csv += "=".repeat(80) + "\n";

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${fileName}_${moment().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="card">
            <div className="card-header py-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 font-20">Detailed Sales Report</h5>
                <div className="d-flex align-items-center gap-3">
                  {/* Selected Branch Indicator */}
                  <div className="text-end">
                    <span className="text-muted small">Selected Branch:</span>
                    <div className="fw-bold text-primary">
                      {selectedBranch ? (
                        branches.find(b => b.id == selectedBranch)?.name || "Unknown"
                      ) : (
                        <span className="text-success">All Branches</span>
                      )}
                    </div>
                  </div>
                  {salesData.length > 0 && (
                    <>
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={exportToPDF}
                        disabled={loading}
                      >
                        <i className="bi bi-file-pdf me-1"></i> Export PDF
                      </button>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={exportToCSV}
                        disabled={loading}
                      >
                        <i className="bi bi-file-earmark-spreadsheet me-1"></i> Export CSV
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="card-body">
              {/* Filters Section */}
              <div className="row mb-4">
                <div className="col-12 col-md-2 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Branch</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedBranch}
                      onChange={(e) => setSelectedBranch(e.target.value)}
                    >
                      <option value="">All Branches</option>
                      {branches.map((branch, idx) => (
                        <option key={idx} value={branch.id}>{branch.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-2 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Category</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedCategory}
                      onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        setSelectedPackage(""); // Reset package when category changes
                      }}
                    >
                      <option value="">All Categories</option>
                      {categories.map((cat, idx) => (
                        <option key={idx} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-2 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Package Name</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedPackage}
                      onChange={(e) => setSelectedPackage(e.target.value)}
                    >
                      <option value="">All Packages</option>
                      {packages
                        .filter(pkg => {
                          if (!selectedCategory) return true;
                          const packageItem = packageCatalog.find(item => item.package_name === pkg)
                            || salesData.find(item => item.package_name === pkg);
                          if (!packageItem) return false;
                          const itemCategoryName = normalizeCategoryName(
                            packageItem.category_name || getCategoryName(packageItem.category)
                          );
                          return itemCategoryName === selectedCategory;
                        })
                        .map((pkg, idx) => (
                          <option key={idx} value={pkg}>{pkg}</option>
                        ))}
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-2 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Duration</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedDuration}
                      onChange={(e) => setSelectedDuration(e.target.value)}
                    >
                      <option value="">All Durations</option>
                      <option value="1">Monthly</option>
                      <option value="3">3 Months</option>
                      <option value="6">6 Months</option>
                      <option value="12">Annual</option>
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-2 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Trainer Name</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedTrainer}
                      onChange={(e) => setSelectedTrainer(e.target.value)}
                    >
                      <option value="">All Trainers</option>
                      {trainers.map((trainer, idx) => (
                        <option key={idx} value={trainer}>{trainer}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-2 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Payment Method</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedPaymentMethod}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    >
                      <option value="">All Payment Methods</option>
                      {paymentMethods.map((method, idx) => (
                        <option key={idx} value={method.id}>{method.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-2 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Sale Type</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedSaleType}
                      onChange={(e) => setSelectedSaleType(e.target.value)}
                    >
                      <option value="">All Sale Types</option>
                      <option value="New">New</option>
                      <option value="Renew">Renew</option>
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-2 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Sold By</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedSeller}
                      onChange={(e) => setSelectedSeller(e.target.value)}
                    >
                      <option value="">All Sellers</option>
                      {sellers.map((seller, idx) => (
                        <option key={idx} value={seller}>{seller}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-2 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Start Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-2 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">End Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Quick Date Filters</label>
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleDateFilter("1month")}
                        title="Last 1 Month"
                      >
                        1 Month
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleDateFilter("2month")}
                        title="Last 2 Months"
                      >
                        2 Months
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleDateFilter("3month")}
                        title="Last 3 Months"
                      >
                        3 Months
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleDateFilter("6month")}
                        title="Last 6 Months"
                      >
                        6 Months
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleDateFilter("quarterly")}
                        title="Quarterly (Last 3 Months)"
                      >
                        Quarterly
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleDateFilter("yearly")}
                        title="Last Year"
                      >
                        Yearly
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-1 mb-3 mb-md-0 d-flex align-items-end">
                  <button
                    className="btn btn-sm btn-primary w-100 me-1"
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </div>
              {(selectedCategory || selectedPackage || selectedDuration || selectedTrainer || selectedPaymentMethod || selectedSaleType || selectedBranch || selectedSeller || startDate || endDate) && (
                <div className="row mb-3">
                  <div className="col-12">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleClearFilters}
                    >
                      <i className="bi bi-x-circle me-1"></i> Clear All Filters
                    </button>
                  </div>
                </div>
              )}
              <hr />

              {/* View Mode Toggle Buttons */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${viewMode === "Detail" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setViewMode("Detail")}
                    >
                      <i className="bi bi-list-ul me-1"></i> Detail
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === "Summary" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setViewMode("Summary")}
                    >
                      <i className="bi bi-bar-chart me-1"></i> Summary
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === "Management" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setViewMode("Management")}
                    >
                      <i className="bi bi-pie-chart me-1"></i> Management View
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === "TrainerWise" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => {
                        setViewMode("TrainerWise");
                        setTrainerWiseMode("summary");
                      }}
                    >
                      <i className="bi bi-person-badge me-1"></i> Trainer Wise
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === "Graphical" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setViewMode("Graphical")}
                    >
                      <i className="bi bi-graph-up me-1"></i> Graphical View
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === "NewVsRenewal" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setViewMode("NewVsRenewal")}
                    >
                      <i className="bi bi-arrow-left-right me-1"></i> New vs Renewal
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === "PackageWise" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setViewMode("PackageWise")}
                    >
                      <i className="bi bi-box-seam me-1"></i> Package Wise
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === "BranchWise" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setViewMode("BranchWise")}
                    >
                      <i className="bi bi-building me-1"></i> Branch Wise
                    </button>
                  </div>
                </div>
              </div>

              {viewMode === "TrainerWise" && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="btn-group" role="group" aria-label="Trainer wise mode">
                      <button
                        type="button"
                        className={`btn ${trainerWiseMode === "summary" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setTrainerWiseMode("summary")}
                      >
                        <i className="bi bi-bar-chart-line me-1"></i> Trainer Summary
                      </button>
                      <button
                        type="button"
                        className={`btn ${trainerWiseMode === "detail" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setTrainerWiseMode("detail")}
                      >
                        <i className="bi bi-table me-1"></i> Trainer Detailed
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary KPI row: Sales excl. GST + GST + Net incl. GST; discount far right */}
              {summaryData && (
                <div className="row mb-4 align-items-stretch g-2">
                  <div className="col-6 col-lg-2 mb-2 mb-lg-0">
                    <div className="card bg-info text-white h-100">
                      <div className="card-body text-center py-3">
                        <h6 className="card-title mb-2 fw-bold">Total Records</h6>
                        <h4 className="mb-0">{summaryData.totalRecords}</h4>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-lg-2 mb-2 mb-lg-0">
                    <div className="card bg-primary text-white h-100">
                      <div className="card-body text-center py-3">
                        <h6 className="card-title mb-2 fw-bold">Total Sales</h6>
                        <p className="mb-1 small fw-semibold">(Excl. GST)</p>
                        <h4 className="mb-0">
                          <CurrencyFormat
                            value={summaryData.totalSales}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                            suffix={""}
                          />
                        </h4>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-lg-2 mb-2 mb-lg-0">
                    <div className="card text-white h-100" style={{ backgroundColor: "#6f42c1" }}>
                      <div className="card-body text-center py-3">
                        <h6 className="card-title mb-2 fw-bold">Total GST</h6>
                        <p className="mb-1 small fw-semibold">(Tax)</p>
                        <h4 className="mb-0">
                          <CurrencyFormat
                            value={summaryData.totalTax}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                            suffix={""}
                          />
                        </h4>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-lg-2 mb-2 mb-lg-0">
                    <div className="card bg-success text-white h-100">
                      <div className="card-body text-center py-3">
                        <h6 className="card-title mb-2 fw-bold">Total Net Price</h6>
                        <p className="mb-1 small fw-semibold">(Incl. GST)</p>
                        <h4 className="mb-0">
                          <CurrencyFormat
                            value={summaryData.totalNetPrice}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                            suffix={""}
                          />
                        </h4>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-lg-2 mb-2 mb-lg-0 ms-lg-auto">
                    <div className="card bg-warning text-dark h-100">
                      <div className="card-body text-center py-3">
                        <h6 className="card-title mb-2 fw-bold">Total Discount</h6>
                        <h4 className="mb-0">
                          <CurrencyFormat
                            value={summaryData.totalDiscount}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                            suffix={""}
                          />
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Data Display based on View Mode */}
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : salesData.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">No sales data found</p>
                </div>
              ) : viewMode === "Detail" ? (
                // Detail View
                <div>
                  {(() => {
                    const groupedData = groupDataByCategoryAndDuration(salesData);
                    const sortedCategories = sortCategories(Object.keys(groupedData));
                    let globalIndex = 1;
                    
                    return sortedCategories.map((categoryName) => {
                      const durationGroups = groupedData[categoryName];
                      const sortedDurations = Object.keys(durationGroups).sort((a, b) => {
                        const order = { "Monthly": 1, "3 Months": 2, "6 Months": 3, "Annual": 4 };
                        const aOrder = order[a] || 99;
                        const bOrder = order[b] || 99;
                        if (aOrder !== bOrder) return aOrder - bOrder;
                        return a.localeCompare(b);
                      });
                      
                      return (
                        <div key={categoryName} className="mb-4">
                          <div className="card shadow-sm mb-3">
                            <div 
                              className="card-header bg-primary text-white py-2 cursor-pointer"
                              onClick={() => toggleCategory(categoryName)}
                              style={{ cursor: 'pointer' }}
                            >
                              <h5 className="mb-0 d-flex align-items-center">
                                <i 
                                  className={`bi ${collapsedCategories.has(categoryName) ? 'bi-chevron-right' : 'bi-chevron-down'} me-2`}
                                  style={{ fontSize: '0.9rem' }}
                                ></i>
                                <i className="bi bi-tag-fill me-2"></i>
                                {categoryName}
                                <span className="badge bg-light text-dark ms-2">
                                  {Object.values(durationGroups).reduce((sum, items) => sum + items.length, 0)} sales
                                </span>
                              </h5>
                            </div>
                          </div>
                          
                          {!collapsedCategories.has(categoryName) && sortedDurations.map((durationName) => {
                            const items = durationGroups[durationName];
                            const categoryIndex = globalIndex;
                            globalIndex += items.length;
                            
                            const durationKey = `${categoryName}-${durationName}`;
                            const isDurationCollapsed = collapsedDurations.has(durationKey);
                            
                            return (
                              <div key={`${categoryName}-${durationName}`} className="mb-3 ms-3">
                                <div className="card border-secondary mb-2">
                                  <div 
                                    className="card-header bg-secondary text-white py-2 cursor-pointer"
                                    onClick={() => toggleDuration(categoryName, durationName)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <h6 className="mb-0 d-flex align-items-center">
                                      <i 
                                        className={`bi ${isDurationCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'} me-2`}
                                        style={{ fontSize: '0.85rem' }}
                                      ></i>
                                      <i className="bi bi-clock me-2"></i>
                                      {durationName}
                                      <span className="badge bg-light text-dark ms-2">{items.length} sales</span>
                                    </h6>
                                  </div>
                                </div>
                                
                                {!isDurationCollapsed && (
                                <div className="table-responsive ms-3">
                                  <table className="table table-bordered table-hover table-sm">
                                    <thead className="table-light">
                                      <tr>
                                        <th>No</th>
                                        <th>Order ID</th>
                                        <th>Branch</th>
                                        <th>Package Name</th>
                                        {((selectedTrainer && selectedTrainer !== "") || categoryName === "Personal Trainer") && (
                                          <th>Trainer</th>
                                        )}
                                        <th>Client Name</th>
                                        <th>Sale Type</th>
                                        <th>Price</th>
                                        <th>Discount</th>
                                        <th>Tax</th>
                                        <th>Net Price</th>
                                        <th>Payment Method</th>
                                        <th>FBR Invoice</th>
                                        <th>Sold By</th>
                                        <th>Sale Date</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {items.map((item, idx) => (
                                        <tr key={item.order_detail_id || idx}>
                                          <td>{categoryIndex + idx}</td>
                                          <td>{item.order_id || "N/A"}</td>
                                          <td>{item.branch_name || "N/A"}</td>
                                          <td>{item.package_name || "N/A"}</td>
                                          {((selectedTrainer && selectedTrainer !== "") || categoryName === "Personal Trainer") && (
                                            <td>{item.trainer_name || "N/A"}</td>
                                          )}
                                          <td>{item.client_name || "N/A"}</td>
                                          <td>
                                            <span className={`badge ${item.sale_type === "New" ? "bg-success" : "bg-info"}`}>
                                              {item.sale_type || "N/A"}
                                            </span>
                                          </td>
                                          <td>
                                            <CurrencyFormat
                                              value={item.order_detail_price || 0}
                                              displayType={"text"}
                                              thousandSeparator={true}
                                              prefix={"Rs "}
                                              suffix={""}
                                            />
                                          </td>
                                          <td>
                                            <CurrencyFormat
                                              value={item.order_detail_discount || 0}
                                              displayType={"text"}
                                              thousandSeparator={true}
                                              prefix={"Rs "}
                                              suffix={""}
                                            />
                                          </td>
                                          <td>
                                            <CurrencyFormat
                                              value={item.order_detail_tax || 0}
                                              displayType={"text"}
                                              thousandSeparator={true}
                                              prefix={"Rs "}
                                              suffix={""}
                                            />
                                          </td>
                                          <td>
                                            <CurrencyFormat
                                              value={item.order_detail_net_price || 0}
                                              displayType={"text"}
                                              thousandSeparator={true}
                                              prefix={"Rs "}
                                              suffix={""}
                                            />
                                          </td>
                                          <td>{item.payment_method || "N/A"}</td>
                                          <td>{item.fbr_invoice_number || "N/A"}</td>
                                          <td>{item.sold_by || "N/A"}</td>
                                          <td>{moment(item.sale_date).format("DD/MM/YYYY")}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot className="table-info fw-bold">
                                      <tr>
                                        <td colSpan={((selectedTrainer && selectedTrainer !== "") || categoryName === "Personal Trainer") ? 7 : 6} className="text-end">Total:</td>
                                        <td>
                                          <CurrencyFormat
                                            value={items.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0)}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            prefix={"Rs "}
                                            suffix={""}
                                          />
                                        </td>
                                        <td>
                                          <CurrencyFormat
                                            value={items.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0)}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            prefix={"Rs "}
                                            suffix={""}
                                          />
                                        </td>
                                        <td>
                                          <CurrencyFormat
                                            value={items.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0)}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            prefix={"Rs "}
                                            suffix={""}
                                          />
                                        </td>
                                        <td>
                                          <CurrencyFormat
                                            value={items.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0)}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            prefix={"Rs "}
                                            suffix={""}
                                          />
                                        </td>
                                        <td>
                                          {(() => {
                                            const allMethods = items
                                              .map(item => item.payment_method)
                                              .filter(Boolean);
                                            const uniqueMethods = [...new Set(allMethods)];
                                            return uniqueMethods.length > 0 ? uniqueMethods.join(", ") : "N/A";
                                          })()}
                                        </td>
                                        <td></td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : viewMode === "Summary" ? (
                // Summary View
                <div>
                  {(() => {
                    const groupedData = groupDataByCategoryAndDuration(salesData);
                    const sortedCategories = sortCategories(Object.keys(groupedData));
                    
                    return sortedCategories.map((categoryName) => {
                      const durationGroups = groupedData[categoryName];
                      const sortedDurations = Object.keys(durationGroups).sort((a, b) => {
                        const order = { "Monthly": 1, "3 Months": 2, "6 Months": 3, "Annual": 4 };
                        const aOrder = order[a] || 99;
                        const bOrder = order[b] || 99;
                        if (aOrder !== bOrder) return aOrder - bOrder;
                        return a.localeCompare(b);
                      });
                      
                      return (
                        <div key={categoryName} className="mb-4">
                          <div className="card shadow-sm mb-3">
                            <div className="card-header bg-primary text-white py-2">
                              <h5 className="mb-0">
                                <i className="bi bi-tag-fill me-2"></i>
                                {categoryName}
                              </h5>
                            </div>
                          </div>
                          
                          <div className="table-responsive ms-3">
                            <table className="table table-bordered table-hover table-sm">
                              <thead className="table-light">
                                <tr>
                                  <th>Duration</th>
                                  <th>Quantity</th>
                                  <th>Total Price</th>
                                  <th>Total Discount</th>
                                  <th>Total Tax</th>
                                  <th>Total Net Price</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortedDurations.map((durationName) => {
                                  const items = durationGroups[durationName];
                                  const quantity = items.length;
                                  const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
                                  const totalDiscount = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
                                  const totalTax = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
                                  const totalNetPrice = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
                                  
                                  return (
                                    <tr key={durationName}>
                                      <td><strong>{durationName}</strong></td>
                                      <td><strong>{quantity}</strong></td>
                                      <td>
                                        <CurrencyFormat
                                          value={totalPrice}
                                          displayType={"text"}
                                          thousandSeparator={true}
                                          prefix={"Rs "}
                                          suffix={""}
                                        />
                                      </td>
                                      <td>
                                        <CurrencyFormat
                                          value={totalDiscount}
                                          displayType={"text"}
                                          thousandSeparator={true}
                                          prefix={"Rs "}
                                          suffix={""}
                                        />
                                      </td>
                                      <td>
                                        <CurrencyFormat
                                          value={totalTax}
                                          displayType={"text"}
                                          thousandSeparator={true}
                                          prefix={"Rs "}
                                          suffix={""}
                                        />
                                      </td>
                                      <td>
                                        <CurrencyFormat
                                          value={totalNetPrice}
                                          displayType={"text"}
                                          thousandSeparator={true}
                                          prefix={"Rs "}
                                          suffix={""}
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot className="table-info fw-bold">
                                <tr>
                                  <td className="text-end"><strong>Category Total:</strong></td>
                                  <td>
                                    <strong>{Object.values(durationGroups).reduce((sum, items) => sum + items.length, 0)}</strong>
                                  </td>
                                  <td>
                                    <CurrencyFormat
                                      value={Object.values(durationGroups).reduce((sum, items) => 
                                        sum + items.reduce((s, item) => s + (parseFloat(item.order_detail_price) || 0), 0), 0)}
                                      displayType={"text"}
                                      thousandSeparator={true}
                                      prefix={"Rs "}
                                      suffix={""}
                                    />
                                  </td>
                                  <td>
                                    <CurrencyFormat
                                      value={Object.values(durationGroups).reduce((sum, items) => 
                                        sum + items.reduce((s, item) => s + (parseFloat(item.order_detail_discount) || 0), 0), 0)}
                                      displayType={"text"}
                                      thousandSeparator={true}
                                      prefix={"Rs "}
                                      suffix={""}
                                    />
                                  </td>
                                  <td>
                                    <CurrencyFormat
                                      value={Object.values(durationGroups).reduce((sum, items) => 
                                        sum + items.reduce((s, item) => s + (parseFloat(item.order_detail_tax) || 0), 0), 0)}
                                      displayType={"text"}
                                      thousandSeparator={true}
                                      prefix={"Rs "}
                                      suffix={""}
                                    />
                                  </td>
                                  <td>
                                    <CurrencyFormat
                                      value={Object.values(durationGroups).reduce((sum, items) => 
                                        sum + items.reduce((s, item) => s + (parseFloat(item.order_detail_net_price) || 0), 0), 0)}
                                      displayType={"text"}
                                      thousandSeparator={true}
                                      prefix={"Rs "}
                                      suffix={""}
                                    />
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : viewMode === "TrainerWise" ? (
                <div>
                  {(() => {
                    const groupedTrainerData = groupDataByTrainer(salesData);

                    const trainerEntries = Object.entries(groupedTrainerData);

                    if (trainerWiseMode === "summary") {
                      return (
                        <div className="card shadow-sm">
                          <div className="card-header bg-primary text-white">
                            <h5 className="mb-0">
                              <i className="bi bi-person-badge-fill me-2"></i>
                              Trainer Wise Summary
                            </h5>
                          </div>
                          <div className="card-body">
                            <div className="table-responsive">
                              <table className="table table-bordered table-hover table-sm align-middle">
                                <thead className="table-light">
                                  <tr>
                                    <th>No</th>
                                    <th>Trainer</th>
                                    <th>Sales</th>
                                    <th>Clients</th>
                                    <th>Total Price</th>
                                    <th>Discount</th>
                                    <th>Tax</th>
                                    <th>Net Price</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {trainerEntries.map(([trainerName, items], idx) => {
                                    const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
                                    const totalDiscount = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
                                    const totalTax = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
                                    const totalNetPrice = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
                                    const uniqueClients = new Set(items.map((item) => item.client_name).filter(Boolean)).size;

                                    return (
                                      <tr key={trainerName}>
                                        <td>{idx + 1}</td>
                                        <td>{trainerName}</td>
                                        <td>{items.length}</td>
                                        <td>{uniqueClients}</td>
                                        <td><CurrencyFormat value={totalPrice} displayType={"text"} thousandSeparator={true} prefix={"Rs "} /></td>
                                        <td><CurrencyFormat value={totalDiscount} displayType={"text"} thousandSeparator={true} prefix={"Rs "} /></td>
                                        <td><CurrencyFormat value={totalTax} displayType={"text"} thousandSeparator={true} prefix={"Rs "} /></td>
                                        <td><CurrencyFormat value={totalNetPrice} displayType={"text"} thousandSeparator={true} prefix={"Rs "} /></td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return trainerEntries.map(([trainerName, items]) => {
                      const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
                      const totalDiscount = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
                      const totalTax = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
                      const totalNetPrice = items.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
                      const uniqueClients = new Set(items.map((item) => item.client_name).filter(Boolean)).size;

                      return (
                        <div key={trainerName} className="card mb-4 shadow-sm">
                          <div className="card-header bg-primary text-white">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                              <h5 className="mb-0">
                                <i className="bi bi-person-badge-fill me-2"></i>
                                {trainerName}
                              </h5>
                              <div className="d-flex flex-wrap gap-3 small">
                                <span>Sales: {items.length}</span>
                                <span>Clients: {uniqueClients}</span>
                                <span>
                                  Net:
                                  {" "}
                                  <CurrencyFormat
                                    value={totalNetPrice}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={"Rs "}
                                  />
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="card-body">
                            <div className="row g-3 mb-3">
                              <div className="col-12 col-md-3">
                                <div className="border rounded p-2 h-100">
                                  <div className="text-muted small">Total Price</div>
                                  <strong>
                                    <CurrencyFormat
                                      value={totalPrice}
                                      displayType={"text"}
                                      thousandSeparator={true}
                                      prefix={"Rs "}
                                    />
                                  </strong>
                                </div>
                              </div>
                              <div className="col-12 col-md-3">
                                <div className="border rounded p-2 h-100">
                                  <div className="text-muted small">Total Discount</div>
                                  <strong>
                                    <CurrencyFormat
                                      value={totalDiscount}
                                      displayType={"text"}
                                      thousandSeparator={true}
                                      prefix={"Rs "}
                                    />
                                  </strong>
                                </div>
                              </div>
                              <div className="col-12 col-md-3">
                                <div className="border rounded p-2 h-100">
                                  <div className="text-muted small">Total Tax</div>
                                  <strong>
                                    <CurrencyFormat
                                      value={totalTax}
                                      displayType={"text"}
                                      thousandSeparator={true}
                                      prefix={"Rs "}
                                    />
                                  </strong>
                                </div>
                              </div>
                              <div className="col-12 col-md-3">
                                <div className="border rounded p-2 h-100">
                                  <div className="text-muted small">Client Count</div>
                                  <strong>{uniqueClients}</strong>
                                </div>
                              </div>
                            </div>

                            <div className="table-responsive">
                              <table className="table table-bordered table-hover table-sm">
                                <thead className="table-light">
                                  <tr>
                                    <th>No</th>
                                    <th>Client</th>
                                    <th>Package</th>
                                    <th>Category</th>
                                    <th>Sale Type</th>
                                    <th>Price</th>
                                    <th>Discount</th>
                                    <th>Tax</th>
                                    <th>Net Price</th>
                                    <th>Payment Method</th>
                                    <th>Sold By</th>
                                    <th>Sale Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((item, idx) => (
                                    <tr key={item.order_detail_id || `${trainerName}-${idx}`}>
                                      <td>{idx + 1}</td>
                                      <td>{item.client_name || "N/A"}</td>
                                      <td>{item.package_name || "N/A"}</td>
                                      <td>{item.category_name || getCategoryName(item.category) || "N/A"}</td>
                                      <td>
                                        <span className={`badge ${item.sale_type === "New" ? "bg-success" : "bg-info"}`}>
                                          {item.sale_type || "N/A"}
                                        </span>
                                      </td>
                                      <td>
                                        <CurrencyFormat value={item.order_detail_price || 0} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
                                      </td>
                                      <td>
                                        <CurrencyFormat value={item.order_detail_discount || 0} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
                                      </td>
                                      <td>
                                        <CurrencyFormat value={item.order_detail_tax || 0} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
                                      </td>
                                      <td>
                                        <CurrencyFormat value={item.order_detail_net_price || 0} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
                                      </td>
                                      <td>{item.payment_method || "N/A"}</td>
                                      <td>{item.sold_by || "N/A"}</td>
                                      <td>{moment(item.sale_date).format("DD/MM/YYYY")}</td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot className="table-info fw-bold">
                                  <tr>
                                    <td colSpan={5} className="text-end">Trainer Total:</td>
                                    <td>
                                      <CurrencyFormat value={totalPrice} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
                                    </td>
                                    <td>
                                      <CurrencyFormat value={totalDiscount} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
                                    </td>
                                    <td>
                                      <CurrencyFormat value={totalTax} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
                                    </td>
                                    <td>
                                      <CurrencyFormat value={totalNetPrice} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
                                    </td>
                                    <td colSpan={3}></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : viewMode === "Graphical" ? (
                // Graphical View
                <DetailedSalesGraphicalView salesData={salesData} />
              ) : viewMode === "PackageWise" ? (
                // Package Wise View
                <PackageWiseSalesReport salesData={salesData} />
              ) : viewMode === "BranchWise" ? (
                // Branch Wise Comparison View
                <BranchWiseComparison salesData={salesData} />
              ) : viewMode === "NewVsRenewal" ? (
                // New vs Renewal Comparison View
                <div>
                  {/* Overall Summary Cards */}
                  <div className="row mb-4">
                    <div className="col-12 col-md-6 mb-3">
                      <div className="card bg-success text-white">
                        <div className="card-body">
                          <h5 className="card-title">
                            <i className="bi bi-check-circle me-2"></i>New Sales
                          </h5>
                          <div className="row">
                            <div className="col-6">
                              <p className="mb-1 small">Total Sales</p>
                              <h4>
                                <CurrencyFormat
                                  value={salesData.filter(item => item.sale_type === "New").reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0)}
                                  displayType={"text"}
                                  thousandSeparator={true}
                                  prefix={"Rs "}
                                />
                              </h4>
                            </div>
                            <div className="col-6">
                              <p className="mb-1 small">Count</p>
                              <h4>{salesData.filter(item => item.sale_type === "New").length}</h4>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 mb-3">
                      <div className="card bg-info text-white">
                        <div className="card-body">
                          <h5 className="card-title">
                            <i className="bi bi-arrow-repeat me-2"></i>Renewal Sales
                          </h5>
                          <div className="row">
                            <div className="col-6">
                              <p className="mb-1 small">Total Sales</p>
                              <h4>
                                <CurrencyFormat
                                  value={salesData.filter(item => item.sale_type === "Renew").reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0)}
                                  displayType={"text"}
                                  thousandSeparator={true}
                                  prefix={"Rs "}
                                />
                              </h4>
                            </div>
                            <div className="col-6">
                              <p className="mb-1 small">Count</p>
                              <h4>{salesData.filter(item => item.sale_type === "Renew").length}</h4>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Category-wise Breakdown */}
                  {(() => {
                    const groupedData = groupDataByCategoryAndDuration(salesData);
                    const sortedCategories = sortCategories(Object.keys(groupedData));
                    
                    return sortedCategories.map((categoryName) => {
                      const durationGroups = groupedData[categoryName];
                      const categoryItems = Object.values(durationGroups).flat();
                      
                      const newSales = categoryItems.filter(item => item.sale_type === "New");
                      const renewSales = categoryItems.filter(item => item.sale_type === "Renew");
                      
                      const newTotalPrice = newSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
                      const newTotalDiscount = newSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
                      const newTotalTax = newSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
                      const newTotalNet = newSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
                      
                      const renewTotalPrice = renewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
                      const renewTotalDiscount = renewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
                      const renewTotalTax = renewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
                      const renewTotalNet = renewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
                      
                      return (
                        <div key={categoryName} className="card mb-4 shadow-sm">
                          <div className="card-header bg-primary text-white">
                            <h5 className="mb-0">
                              <i className="bi bi-tag-fill me-2"></i>
                              {categoryName}
                            </h5>
                          </div>
                          <div className="card-body">
                            {/* Category Summary */}
                            <div className="row mb-3">
                              <div className="col-12 col-md-6 mb-3">
                                <div className="card border-success">
                                  <div className="card-header bg-success text-white">
                                    <strong>New Sales</strong>
                                  </div>
                                  <div className="card-body">
                                    <div className="row">
                                      <div className="col-6">
                                        <p className="mb-1 small text-muted">Count</p>
                                        <h5>{newSales.length}</h5>
                                      </div>
                                      <div className="col-6">
                                        <p className="mb-1 small text-muted">Total Net Price</p>
                                        <h5>
                                          <CurrencyFormat
                                            value={newTotalNet}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            prefix={"Rs "}
                                          />
                                        </h5>
                                      </div>
                                    </div>
                                    <hr />
                                    <div className="row">
                                      <div className="col-4">
                                        <p className="mb-0 small text-muted">Price</p>
                                        <CurrencyFormat
                                          value={newTotalPrice}
                                          displayType={"text"}
                                          thousandSeparator={true}
                                          prefix={"Rs "}
                                        />
                                      </div>
                                      <div className="col-4">
                                        <p className="mb-0 small text-muted">Discount</p>
                                        <CurrencyFormat
                                          value={newTotalDiscount}
                                          displayType={"text"}
                                          thousandSeparator={true}
                                          prefix={"Rs "}
                                        />
                                      </div>
                                      <div className="col-4">
                                        <p className="mb-0 small text-muted">Tax</p>
                                        <CurrencyFormat
                                          value={newTotalTax}
                                          displayType={"text"}
                                          thousandSeparator={true}
                                          prefix={"Rs "}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <div className="card border-info">
                                  <div className="card-header bg-info text-white">
                                    <strong>Renewal Sales</strong>
                                  </div>
                                  <div className="card-body">
                                    <div className="row">
                                      <div className="col-6">
                                        <p className="mb-1 small text-muted">Count</p>
                                        <h5>{renewSales.length}</h5>
                                      </div>
                                      <div className="col-6">
                                        <p className="mb-1 small text-muted">Total Net Price</p>
                                        <h5>
                                          <CurrencyFormat
                                            value={renewTotalNet}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            prefix={"Rs "}
                                          />
                                        </h5>
                                      </div>
                                    </div>
                                    <hr />
                                    <div className="row">
                                      <div className="col-4">
                                        <p className="mb-0 small text-muted">Price</p>
                                        <CurrencyFormat
                                          value={renewTotalPrice}
                                          displayType={"text"}
                                          thousandSeparator={true}
                                          prefix={"Rs "}
                                        />
                                      </div>
                                      <div className="col-4">
                                        <p className="mb-0 small text-muted">Discount</p>
                                        <CurrencyFormat
                                          value={renewTotalDiscount}
                                          displayType={"text"}
                                          thousandSeparator={true}
                                          prefix={"Rs "}
                                        />
                                      </div>
                                      <div className="col-4">
                                        <p className="mb-0 small text-muted">Tax</p>
                                        <CurrencyFormat
                                          value={renewTotalTax}
                                          displayType={"text"}
                                          thousandSeparator={true}
                                          prefix={"Rs "}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Detailed Breakdown by Duration */}
                            {Object.keys(durationGroups).sort((a, b) => {
                              const order = { "Monthly": 1, "3 Months": 2, "6 Months": 3, "Annual": 4 };
                              const aOrder = order[a] || 99;
                              const bOrder = order[b] || 99;
                              if (aOrder !== bOrder) return aOrder - bOrder;
                              return a.localeCompare(b);
                            }).map((durationName) => {
                              const items = durationGroups[durationName];
                              const durationNewSales = items.filter(item => item.sale_type === "New");
                              const durationRenewSales = items.filter(item => item.sale_type === "Renew");
                              
                              return (
                                <div key={durationName} className="mb-3">
                                  <div className="card border-secondary">
                                    <div className="card-header bg-secondary text-white">
                                      <h6 className="mb-0">
                                        <i className="bi bi-clock me-2"></i>
                                        {durationName}
                                      </h6>
                                    </div>
                                    <div className="card-body p-2">
                                      <div className="table-responsive">
                                        <table className="table table-sm table-bordered mb-0">
                                          <thead className="table-light">
                                            <tr>
                                              <th>Sale Type</th>
                                              <th>Count</th>
                                              <th>Total Price</th>
                                              <th>Total Discount</th>
                                              <th>Total Tax</th>
                                              <th>Total Net Price</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <tr className="table-success">
                                              <td><span className="badge bg-success">New</span></td>
                                              <td>{durationNewSales.length}</td>
                                              <td>
                                                <CurrencyFormat
                                                  value={durationNewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0)}
                                                  displayType={"text"}
                                                  thousandSeparator={true}
                                                  prefix={"Rs "}
                                                />
                                              </td>
                                              <td>
                                                <CurrencyFormat
                                                  value={durationNewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0)}
                                                  displayType={"text"}
                                                  thousandSeparator={true}
                                                  prefix={"Rs "}
                                                />
                                              </td>
                                              <td>
                                                <CurrencyFormat
                                                  value={durationNewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0)}
                                                  displayType={"text"}
                                                  thousandSeparator={true}
                                                  prefix={"Rs "}
                                                />
                                              </td>
                                              <td>
                                                <CurrencyFormat
                                                  value={durationNewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0)}
                                                  displayType={"text"}
                                                  thousandSeparator={true}
                                                  prefix={"Rs "}
                                                />
                                              </td>
                                            </tr>
                                            <tr className="table-info">
                                              <td><span className="badge bg-info">Renew</span></td>
                                              <td>{durationRenewSales.length}</td>
                                              <td>
                                                <CurrencyFormat
                                                  value={durationRenewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0)}
                                                  displayType={"text"}
                                                  thousandSeparator={true}
                                                  prefix={"Rs "}
                                                />
                                              </td>
                                              <td>
                                                <CurrencyFormat
                                                  value={durationRenewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0)}
                                                  displayType={"text"}
                                                  thousandSeparator={true}
                                                  prefix={"Rs "}
                                                />
                                              </td>
                                              <td>
                                                <CurrencyFormat
                                                  value={durationRenewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0)}
                                                  displayType={"text"}
                                                  thousandSeparator={true}
                                                  prefix={"Rs "}
                                                />
                                              </td>
                                              <td>
                                                <CurrencyFormat
                                                  value={durationRenewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0)}
                                                  displayType={"text"}
                                                  thousandSeparator={true}
                                                  prefix={"Rs "}
                                                />
                                              </td>
                                            </tr>
                                            <tr className="table-warning fw-bold">
                                              <td>Total</td>
                                              <td>{items.length}</td>
                                              <td>
                                                <CurrencyFormat
                                                  value={items.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0)}
                                                  displayType={"text"}
                                                  thousandSeparator={true}
                                                  prefix={"Rs "}
                                                />
                                              </td>
                                              <td>
                                                <CurrencyFormat
                                                  value={items.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0)}
                                                  displayType={"text"}
                                                  thousandSeparator={true}
                                                  prefix={"Rs "}
                                                />
                                              </td>
                                              <td>
                                                <CurrencyFormat
                                                  value={items.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0)}
                                                  displayType={"text"}
                                                  thousandSeparator={true}
                                                  prefix={"Rs "}
                                                />
                                              </td>
                                              <td>
                                                <CurrencyFormat
                                                  value={items.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0)}
                                                  displayType={"text"}
                                                  thousandSeparator={true}
                                                  prefix={"Rs "}
                                                />
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                // Management View
                <div>
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover table-striped">
                      <thead className="table-primary">
                        <tr>
                          <th rowSpan={2}>Category</th>
                          <th colSpan={2}>Total Price</th>
                          <th colSpan={2}>Total Discount</th>
                          <th colSpan={2}>Total Tax</th>
                          <th colSpan={2}>Total Net Price</th>
                          <th colSpan={2}>Total</th>
                        </tr>
                        <tr>
                          <th className="bg-success text-white">New</th>
                          <th className="bg-info text-white">Renew</th>
                          <th className="bg-success text-white">New</th>
                          <th className="bg-info text-white">Renew</th>
                          <th className="bg-success text-white">New</th>
                          <th className="bg-info text-white">Renew</th>
                          <th className="bg-success text-white">New</th>
                          <th className="bg-info text-white">Renew</th>
                          <th className="bg-warning">Price</th>
                          <th className="bg-warning">Net Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const groupedData = groupDataByCategoryAndDuration(salesData);
                          const sortedCategories = sortCategories(Object.keys(groupedData));
                          
                          return sortedCategories.map((categoryName) => {
                            const durationGroups = groupedData[categoryName];
                            const categoryItems = Object.values(durationGroups).flat();
                            
                            const newSales = categoryItems.filter(item => item.sale_type === "New");
                            const renewSales = categoryItems.filter(item => item.sale_type === "Renew");
                            
                            const newTotalPrice = newSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
                            const newTotalDiscount = newSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
                            const newTotalTax = newSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
                            const newTotalNetPrice = newSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
                            
                            const renewTotalPrice = renewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
                            const renewTotalDiscount = renewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
                            const renewTotalTax = renewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
                            const renewTotalNetPrice = renewSales.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
                            
                            const categoryTotalPrice = newTotalPrice + renewTotalPrice;
                            const categoryTotalNetPrice = newTotalNetPrice + renewTotalNetPrice;
                            
                            return (
                              <tr key={categoryName}>
                                <td><strong>{categoryName}</strong></td>
                                <td className="bg-light">
                                  <CurrencyFormat
                                    value={newTotalPrice}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={"Rs "}
                                    suffix={""}
                                  />
                                </td>
                                <td className="bg-light">
                                  <CurrencyFormat
                                    value={renewTotalPrice}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={"Rs "}
                                    suffix={""}
                                  />
                                </td>
                                <td className="bg-light">
                                  <CurrencyFormat
                                    value={newTotalDiscount}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={"Rs "}
                                    suffix={""}
                                  />
                                </td>
                                <td className="bg-light">
                                  <CurrencyFormat
                                    value={renewTotalDiscount}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={"Rs "}
                                    suffix={""}
                                  />
                                </td>
                                <td className="bg-light">
                                  <CurrencyFormat
                                    value={newTotalTax}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={"Rs "}
                                    suffix={""}
                                  />
                                </td>
                                <td className="bg-light">
                                  <CurrencyFormat
                                    value={renewTotalTax}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={"Rs "}
                                    suffix={""}
                                  />
                                </td>
                                <td className="bg-light">
                                  <CurrencyFormat
                                    value={newTotalNetPrice}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={"Rs "}
                                    suffix={""}
                                  />
                                </td>
                                <td className="bg-light">
                                  <CurrencyFormat
                                    value={renewTotalNetPrice}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={"Rs "}
                                    suffix={""}
                                  />
                                </td>
                                <td className="bg-warning">
                                  <CurrencyFormat
                                    value={categoryTotalPrice}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={"Rs "}
                                    suffix={""}
                                  />
                                </td>
                                <td className="bg-warning">
                                  <CurrencyFormat
                                    value={categoryTotalNetPrice}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={"Rs "}
                                    suffix={""}
                                  />
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                      <tfoot className="table-success fw-bold">
                        <tr>
                          <td className="text-end"><strong>Grand Total:</strong></td>
                          <td>
                            <CurrencyFormat
                              value={salesData.filter(item => item.sale_type === "New").reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0)}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={""}
                            />
                          </td>
                          <td>
                            <CurrencyFormat
                              value={salesData.filter(item => item.sale_type === "Renew").reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0)}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={""}
                            />
                          </td>
                          <td>
                            <CurrencyFormat
                              value={salesData.filter(item => item.sale_type === "New").reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0)}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={""}
                            />
                          </td>
                          <td>
                            <CurrencyFormat
                              value={salesData.filter(item => item.sale_type === "Renew").reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0)}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={""}
                            />
                          </td>
                          <td>
                            <CurrencyFormat
                              value={salesData.filter(item => item.sale_type === "New").reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0)}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={""}
                            />
                          </td>
                          <td>
                            <CurrencyFormat
                              value={salesData.filter(item => item.sale_type === "Renew").reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0)}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={""}
                            />
                          </td>
                          <td>
                            <CurrencyFormat
                              value={salesData.filter(item => item.sale_type === "New").reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0)}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={""}
                            />
                          </td>
                          <td>
                            <CurrencyFormat
                              value={salesData.filter(item => item.sale_type === "Renew").reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0)}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={""}
                            />
                          </td>
                          <td>
                            <CurrencyFormat
                              value={salesData.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0)}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={""}
                            />
                          </td>
                          <td>
                            <CurrencyFormat
                              value={salesData.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0)}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={""}
                            />
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Grand totals at the very end of the page (Detail view only) */}
              {viewMode === "Detail" && salesData.length > 0 && (
                <div className="card mt-3">
                  <div className="card-header bg-light">
                    <strong>Grand Totals</strong>
                  </div>
                  <div className="card-body p-2">
                    <div className="row g-2">
                      <div className="col-6 col-md-2">
                        <div className="border rounded p-2 text-center">
                          <div className="text-muted small">Total Price</div>
                          <div>
                            <CurrencyFormat value={salesData.reduce((s,i)=>s+(parseFloat(i.order_detail_price)||0),0)} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
                          </div>
                        </div>
                      </div>
                      <div className="col-6 col-md-2">
                        <div className="border rounded p-2 text-center">
                          <div className="text-muted small">Total Discount</div>
                          <div>
                            <CurrencyFormat value={salesData.reduce((s,i)=>s+(parseFloat(i.order_detail_discount)||0),0)} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
                          </div>
                        </div>
                      </div>
                      <div className="col-6 col-md-2">
                        <div className="border rounded p-2 text-center">
                          <div className="text-muted small">Total Tax</div>
                          <div>
                            <CurrencyFormat value={salesData.reduce((s,i)=>s+(parseFloat(i.order_detail_tax)||0),0)} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
                          </div>
                        </div>
                      </div>
                      <div className="col-6 col-md-2">
                        <div className="border rounded p-2 text-center">
                          <div className="text-muted small">Total Net Price</div>
                          <div>
                            <CurrencyFormat value={salesData.reduce((s,i)=>s+(parseFloat(i.order_detail_net_price)||0),0)} displayType={"text"} thousandSeparator={true} prefix={"Rs "} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <div className={`loader ${loading ? "in" : ""}`}>
        <div className="spinner-border main-spin"></div>
      </div>
    </div>
  );
};

export default DetailedSalesReport;

